import { apiRequest } from "./client";

/**
 * NestJS deals/opportunities + pipeline contract (Part 72 §72.3):
 * - GET  /api/deals?q=&page=&size=          → { items, total, page, size }
 * - POST /api/deals                         → deal
 * - PATCH /api/deals/:id                    → deal
 * - PATCH /api/deals/:id/stage              → move stage (open stages only)
 * - POST /api/deals/:id/close               → won → isWon stage, lost → lost stage + lossReason
 * - DELETE /api/deals/:id                   → 200
 * - GET  /api/pipeline/config               → { stages: [{id,name,probability,isWon,isClosed}], wonStage, lostStage }
 * - POST/PATCH/DELETE /api/pipeline/stages  → org-scoped stage CRUD (Owner/Manager)
 * - GET  /api/pipeline/board                → { columns: [{stage,name,deals}] }
 * Stage is a 1-based int into the org's pipeline config on the wire (legacy
 * parity, Part 58 §58.5). Stats are derived from real list data; won/lost
 * semantics come from the config flags (fallback 5/6 = defaults).
 */
const LIST_SIZE = 200;

export const STAGE_LABELS = {
  1: "Prospecting",
  2: "Qualification",
  3: "Proposal",
  4: "Negotiation",
  5: "Closed Won",
  6: "Closed Lost",
};

export const STAGE_ENUMS = {
  Prospecting: 1,
  Qualification: 2,
  Proposal: 3,
  Negotiation: 4,
  "Closed Won": 5,
  ClosedWon: 5,
  "Closed Lost": 6,
  ClosedLost: 6,
};

export function stageLabel(stage) {
  if (typeof stage === "string") return stage;
  return STAGE_LABELS[stage] || "Prospecting";
}

export function stageEnum(stageLabelOrInt) {
  if (typeof stageLabelOrInt === "number") return stageLabelOrInt;
  return STAGE_ENUMS[stageLabelOrInt] || 1;
}

/** Deal wire → shape the existing opportunity/pipeline UI already understands. */
export function mapDealFromApi(deal) {
  return {
    id: deal?.id,
    name: deal?.name || "",
    accountName: deal?.accountName || "",
    ownerName: deal?.ownerName || "",
    stage: deal?.stage ?? 1,
    amount: Number(deal?.amount ?? deal?.amountExTax ?? 0),
    probabilityPercent: Number(deal?.probabilityPercent || 0),
    expectedCloseDateUtc: deal?.expectedCloseDateUtc || null,
    description: deal?.description || null,
    stageHistory: deal?.stageHistory || [],
    lossReason: deal?.lossReason || null,
    closedAtUtc: deal?.closedAtUtc || null,
    updatedAtUtc: deal?.updatedAtUtc || null,
  };
}

function deriveStats(deals, flags = {}) {
  // Sprint 15 QA: won/lost stages are closed, not open — pipeline value must
  // exclude closed revenue; flag-driven so custom pipelines count correctly.
  const { wonStage = 5, lostStage = 6 } = flags;
  const open = deals.filter((d) => d.stage !== wonStage && d.stage !== lostStage);
  const closed = deals.filter((d) => d.stage === wonStage || d.stage === lostStage);
  const won = deals.filter((d) => d.stage === wonStage);

  const totalOpportunities = deals.length;
  const pipelineValue = open.reduce((sum, d) => sum + d.amount, 0);
  const averageDealSize = open.length ? Math.round(open.reduce((sum, d) => sum + d.amount, 0) / open.length) : 0;
  const winRatePercent = closed.length ? Number(((won.length / closed.length) * 100).toFixed(1)) : 0;

  return { totalOpportunities, pipelineValue, averageDealSize, winRatePercent };
}

/**
 * Moves a deal to a stage. Won/lost stages are terminal — they go through the
 * close endpoint. Flag-driven (Sprint 15 QA): with custom pipelines the won
 * stage is not always 5, so callers pass { wonStage, lostStage } from the
 * pipeline config (fallback 5/6 = defaults).
 */
async function moveStage(id, stage, flags = {}) {
  const { wonStage = 5, lostStage = 6 } = flags;
  if (stage === wonStage) {
    return apiRequest(`/api/deals/${id}/close`, {
      method: "POST",
      body: JSON.stringify({ result: "won" }),
    });
  }
  if (stage === lostStage) {
    return apiRequest(`/api/deals/${id}/close`, {
      method: "POST",
      body: JSON.stringify({ result: "lost", lossReason: "other" }),
    });
  }
  return apiRequest(`/api/deals/${id}/stage`, {
    method: "PATCH",
    body: JSON.stringify({ stage }),
  });
}

export const dealsApi = {
  getAll: async (q, opts = {}) => {
    const params = new URLSearchParams({ page: "1", size: String(LIST_SIZE) });
    if (q?.trim()) params.set("q", q.trim());
    if (opts?.contactId) params.set("contactId", opts.contactId);
    const data = await apiRequest(`/api/deals?${params}`);
    return (data?.items || []).map(mapDealFromApi);
  },
  getStats: async (flags) => deriveStats(await dealsApi.getAll(), flags),
  create: (payload) =>
    apiRequest("/api/deals", { method: "POST", body: JSON.stringify(payload) }),
  update: (id, payload) =>
    apiRequest(`/api/deals/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  remove: (id) => apiRequest(`/api/deals/${id}`, { method: "DELETE" }),
  moveStage,
  close: (id, result, lossReason) =>
    apiRequest(`/api/deals/${id}/close`, {
      method: "POST",
      body: JSON.stringify({ result, lossReason }),
    }),
};

/** Board: NestJS { columns:[{stage,name,isClosed,isWon,deals}] } → { "<stage-int>": [cards] }. */
function mapBoard(board) {
  const mapped = {};
  for (const column of board?.columns || []) {
    mapped[String(column.stage)] = (column.deals || []).map((card) => ({
      id: card.id,
      name: card.name,
      accountName: card.accountName ?? null,
      amount: Number(card.amountIncTax ?? 0),
      probabilityPercent: Number(card.probabilityPercent ?? 0),
      expectedCloseDateUtc: card.expectedCloseDateUtc ?? null,
      ownerName: card.ownerName ?? null,
      stage: column.stage,
      timeInStageDays: card.timeInStageDays ?? 0,
      description: card.description ?? null,
    }));
  }
  return mapped;
}

function deriveMetrics(columns, flags = {}) {
  const all = Object.values(columns || {}).flat();
  const { wonStage = 5, lostStage = 6 } = flags;
  const open = all.filter((d) => d.stage !== wonStage && d.stage !== lostStage);
  const closed = all.filter((d) => d.stage === wonStage || d.stage === lostStage);
  const won = all.filter((d) => d.stage === wonStage);

  const totalPipeline = open.reduce((sum, d) => sum + d.amount, 0);
  const dealsInPipeline = open.length;
  const averageDealSize = open.length ? Math.round(totalPipeline / open.length) : 0;
  const winRatePercent = closed.length ? Number(((won.length / closed.length) * 100).toFixed(1)) : 0;
  const averageSalesCycleDays = all.length
    ? Math.round(all.reduce((sum, d) => sum + (d.timeInStageDays || 0), 0) / all.length)
    : 0;
  const forecastedRevenue = totalPipeline;

  return { totalPipeline, dealsInPipeline, averageDealSize, winRatePercent, averageSalesCycleDays, forecastedRevenue };
}

/** Opportunities screen — same entity as deals (Part 72 §72.3). */
export const opportunitiesApi = {
  getStats: (flags) => dealsApi.getStats(flags),
  getAll: dealsApi.getAll,
  create: dealsApi.create,
  update: dealsApi.update,
  remove: dealsApi.remove,
  moveStage: (id, stage, flags) => moveStage(id, stageEnum(stage), flags),
};

export const pipelineApi = {
  getBoard: async () => {
    const board = await apiRequest("/api/pipeline/board");
    return mapBoard(board);
  },
  getMetrics: async (flags) => deriveMetrics(await pipelineApi.getBoard(), flags),
  getConfig: () => apiRequest("/api/pipeline/config"),
  createDeal: (payload) =>
    apiRequest("/api/deals", { method: "POST", body: JSON.stringify(payload) }),
  updateDeal: (id, payload) =>
    apiRequest(`/api/deals/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  removeDeal: (id) => apiRequest(`/api/deals/${id}`, { method: "DELETE" }),
  moveDealStage: (id, stage, flags) => moveStage(id, stageEnum(stage), flags),
};

/** Sprint 15 QA — org-scoped pipeline stage management (Owner/Manager). */
export const stagesApi = {
  addStage: (name, probability) =>
    apiRequest("/api/pipeline/stages", {
      method: "POST",
      body: JSON.stringify({ name, probability: Number(probability || 0) }),
    }),
  updateStage: (id, patch) =>
    apiRequest(`/api/pipeline/stages/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  removeStage: (id) => apiRequest(`/api/pipeline/stages/${id}`, { method: "DELETE" }),
};
